import React from "react"
import { LexicalText } from "@/schema"
import { cn } from "@ui/utils"
import DOMPurify from "dompurify"

import PeekView from "@/components/peek-view"

interface LexicalNode {
  type: string
  text?: string
  children?: LexicalNode[]
  recordType?: string
  recordId?: string
  format?: number
  listType?: "bullet" | "number" | "check"
  checked?: boolean
}

interface LexicalPreviewProps {
  content?: LexicalText
  className?: string
  inline?: boolean
}
export function LexicalPreview({ content, className, inline = false }: LexicalPreviewProps) {
  // If no content at all, return null
  if (!content) return null

  // If we have markdown or html but no nodes, render the content directly
  if (!content.nodes?.root?.children) {
    const Tag = inline ? "span" : "div"

    // Prefer markdown as plain text if it exists
    if (content.markdown) {
      return <Tag className={cn(inline ? "" : "whitespace-pre-wrap", className)}>{content.markdown}</Tag>
    }

    // Render HTML as actual HTML if that's all we have
    if (content.html) {
      return (
        <Tag
          className={cn(inline ? "" : "whitespace-pre-wrap", className)}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content.html) }}
        />
      )
    }

    return null
  }

  const renderNode = (node: LexicalNode, key: number): React.JSX.Element | string | null => {
    if (node.type === "text" || node.type === "extended-text") {
      let textElement: React.ReactNode = node.text || ""
      if (node.format === 1) textElement = <strong>{textElement}</strong>
      if (node.format === 2) textElement = <em>{textElement}</em>
      return <React.Fragment key={key}>{textElement}</React.Fragment>
    } else if (node.type === "mention") {
      if (node.recordType && node.recordId && node.text) {
        return (
          <PeekView
            key={key}
            label={node.text}
            recordType={node.recordType as "Client" | "Prospect" | "User"}
            recordId={node.recordId}
            size="sm"
          />
        )
      }
      return <span key={key}>{node.text}</span>
    } else if (node.type === "linebreak") {
      return <br key={key} />
    } else if (node.children && node.children.length > 0) {
      const children = node.children.map((child, childKey) => renderNode(child, childKey)).filter(Boolean)
      if (children.length === 0) return null

      switch (node.type) {
        case "paragraph":
          return inline ? <React.Fragment key={key}>{children}</React.Fragment> : <p key={key}>{children}</p>
        case "listitem":
          return (
            <li
              key={key}
              className={
                node.checked === true
                  ? "text-muted-foreground list-none line-through"
                  : node.checked === false
                    ? "list-none"
                    : undefined
              }
            >
              {typeof node.checked === "boolean" && (
                <span role="img" aria-label={node.checked ? "Checked" : "Unchecked"} className="mr-1 no-underline">
                  {node.checked ? "☑" : "☐"}
                </span>
              )}
              {children}
            </li>
          )
        case "list":
          return node.listType === "number" ? <ol key={key}>{children}</ol> : <ul key={key}>{children}</ul>
        default:
          return <React.Fragment key={key}>{children}</React.Fragment>
      }
    }
    return null
  }

  const renderedNodes = content.nodes.root.children.map((node, key) => renderNode(node, key)).filter(Boolean)

  const Tag = inline ? "span" : "div"

  return (
    <Tag
      className={cn(inline ? "" : "whitespace-pre-wrap", className)}
      style={{
        fontFamily: "inherit",
        wordBreak: "break-word",
      }}
    >
      {renderedNodes}
    </Tag>
  )
}
